/** The walk region of the Decide page: the step being walked, or, once every open decision is handled, the finished state. DecidePage supplies the page's own pieces as props. */
import type { ReactNode } from "react";
import type { ContentCatalog, DecisionCatalogEntry, DraftView, MarketView, TeamState } from "@bsim/contracts";
import type { DecisionFamily, DecisionId, Decisions, PreviewTrace } from "@bsim/domain";
import type { RiskProjection } from "../../api/riskFields";
import type { DecisionAccess } from "../DecisionControl";
import { DecisionChips } from "../DecisionChips";
import type { Swap } from "../../financials/swaps";
import { deskOrder } from "../labels";
import { decisionReviewState, reviewTotals } from "../reviewState";
import { handledLine } from "../tracker/Tracker";
import { TRADES_DECISION, TradesBehind } from "../TradesBehind";
import { DriverPanel } from "../drivers/DriverPanel";
import { BeforeYouDecide } from "./BeforeYouDecide";
import type { useWalkPosition } from "./useWalkPosition";
import { Walk } from "./Walk";
import type { WalkStep } from "./walkSteps";

export interface WalkFaceProps {
  readonly walkDraft: DraftView; readonly catalog: ContentCatalog; readonly walk: ReturnType<typeof useWalkPosition>;
  readonly team: TeamState; readonly market: MarketView; readonly projection: RiskProjection | undefined; readonly trace: PreviewTrace | null;
  readonly decisions: Decisions; readonly seed: Partial<Decisions>; readonly lastYear: Partial<Decisions> | null; readonly carried: boolean; readonly readOnly: boolean;
  readonly entryOf: (id: string) => DecisionCatalogEntry; readonly accessOf: (id: string) => DecisionAccess;
  readonly whoSet: (entry: DecisionCatalogEntry) => ReactNode;
  readonly controlFor: (id: string) => ReactNode; readonly onMaintain: Parameters<typeof Walk>[0]["onMaintain"];
  /** The desk's holder when a teammate has it, with the way to take it over. */
  readonly held: { readonly desk: DecisionFamily; readonly deskName: string; readonly holder: string; readonly onTakeOver: () => void } | null;
  /** The swaps held at round start: a swap-trading row says it holds them. */
  readonly heldSwaps?: readonly Swap[] | undefined;
  readonly notes: ReactNode; readonly rationale: (asked: boolean) => ReactNode; readonly advanceRef?: Parameters<typeof Walk>[0]["advanceRef"]; readonly cards: ReactNode; readonly thesis: ReactNode;
  readonly onStep: (s: WalkStep | null) => void;
  /** The page's review action and its label (null when the round is not open: nothing to go to). */
  readonly review: { readonly label: string; readonly go: () => void } | null;
}

export function WalkFace(p: WalkFaceProps) {
  const { walkDraft, catalog, walk } = p;
  const step = walk.current;
  if (step === null) {
    const totals = reviewTotals(walkDraft, deskOrder(catalog), catalog);
    return (
      <div className="gx-walk-grid" data-testid="decide.walk">
        <section className="gx-card gx-walk gx-walk-done" data-testid="walk.done">
          <div className="gx-card-b gx-walk-done-b">
            <div>
              <h2 className="gx-h3 gx-walk-title">Every open decision is handled</h2>
              <p className="gx-small gx-muted" data-testid="walk.done.counts">{handledLine(totals)}</p>
            </div>
            {p.review === null ? null : <button type="button" className="gx-btn gx-btn--primary" data-testid="walk.done.review" onClick={p.review.go}>{p.review.label}</button>}
          </div>
        </section>
      </div>
    );
  }
  const sentence = (id: string): ReactNode => {
    const entry = p.entryOf(id);
    return (
      <>
        <DecisionChips entry={entry} decisions={p.decisions} seed={p.seed} state={decisionReviewState(walkDraft, id as DecisionId)} held={p.accessOf(id).kind === "policy"} who={p.whoSet(entry)} heldSwaps={p.heldSwaps} />
        {p.held === null ? null : <span className="gx-small" data-testid="decide.lock">{p.held.holder} is editing {p.held.deskName} · <button type="button" className="gx-decide-linkbtn" data-testid="decide.takeover" onClick={p.held.onTakeOver}>Take over</button></span>}
      </>
    );
  };
  const kind = step.kind === "decision" ? p.accessOf(step.decisionId).kind : "readonly";
  return (
    <Walk draft={walkDraft} catalog={catalog} steps={walk.steps} current={step} onStep={p.onStep}
      canMark={!p.readOnly && (kind === "open" || kind === "constrained")} onMaintain={p.onMaintain}
      controlFor={(id) => <div aria-disabled={p.held !== null || undefined}>{p.controlFor(id)}{id === TRADES_DECISION ? <TradesBehind trace={p.trace} /> : null}</div>}
      sentence={sentence} deskNotes={() => p.notes} rationale={(_, asked) => p.rationale(asked)} advanceRef={p.advanceRef} cards={p.cards} thesis={p.thesis}
      box={(entry) => <><DriverPanel entry={entry} team={p.team} catalog={catalog} access={p.accessOf(entry.decisionId)} /><BeforeYouDecide entry={entry} team={p.team} catalog={catalog} market={p.market} projection={p.projection} /></>} />
  );
}
